import { useContext } from "react";
import { Collapse, ListItemText, Typography } from "@mui/material";
import { SidebarType } from "./sidebar.types";
import { SidebarContext } from "core/contexts/sidebar/sidebar";
// import "./sidebar.css";
import {
  CustomListItemContainer,
  CustomListItemIcon,
  SideBarCollapsedItems,
  SidebarItemContainer,
} from "./sidebar.styled";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import SideBarToolTip from "./sidebarTooltip";
import SideBarCyberPhysical from "assets/SideBarCyberPhysical";
import SideBarCyberProtection from "assets/SideBarCyberProtection";
import SideBarCyberResilience from "assets/SideBarCyberResilience";
import SideBarCyberStrategy from "assets/SideBarCyberStrategy";
import { useAppDispatch, useAppSelector } from "core/store/reduxStore";
import { setActiveModel } from "modules/chat/store/conversationList";
import SideBarCyberNext from "assets/SideBarCyberNext";
import SideBarCyberAdmin from "assets/SideBarCyberAdmin";
import SideBarCyberChat from "assets/SideBarCyberChat";
import SideBarInsightPortal from "assets/SideBarInsightPortal";
import { navigateToPath } from "core/utils/common";


type Props = {
  item: SidebarType;
  userGroups: string[];
  isDrawerOpen: Boolean;
};

const renderIcon = (iconName, isDrawerOpen, textColor) => {
  switch (iconName) {
    case "admin":
      return (
        <span>
          <SideBarCyberAdmin isDrawerOpen={isDrawerOpen} textColor={textColor}/>
        </span>
      );
    case "Shield":
      return <SideBarCyberProtection isDrawerOpen={isDrawerOpen} textColor={textColor}/>;
    case "RefreshCcw":
      return <SideBarCyberResilience isDrawerOpen={isDrawerOpen} textColor={textColor}/>;
    case "Brain":
      return <SideBarCyberStrategy isDrawerOpen={isDrawerOpen} textColor={textColor}/>;
    case "Factory":
      return <SideBarCyberPhysical isDrawerOpen={isDrawerOpen} textColor={textColor}/>;
    case "Rocket":
      return <SideBarCyberNext isDrawerOpen={isDrawerOpen} textColor={textColor}/>;
    case "Insight":
      return <SideBarInsightPortal isDrawerOpen={isDrawerOpen} textColor={textColor}/>;
    case "MessageSquare":
      return <SideBarCyberChat isDrawerOpen={isDrawerOpen} textColor={textColor}/>;
    default:
      return null;
  }
};

const SidebarItems = ({ item, userGroups, isDrawerOpen: isDrawerOpenProp }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    activeItem,
    setActiveItem,
    expandedNodes,
    setExpandedNodes,
    setSidebarActiveState,
    isDrawerOpen,
    setIsDrawerOpen,
  } = useContext(SidebarContext);

  const convoState = useAppSelector((state) => state.chatConversations);
  const textColor = "#ffffff";

  const handleIconToggle = (nodeId: string) => {
    setIsDrawerOpen(true);
  };

  // Function to find a nicename by provider name
  const findNicenameByProviderName = (providerName: string) => {
    const provider = convoState.providers.find((provider) => provider.name === providerName);
    return provider ? provider.options.nice_name : 'Provider not found';
  };

  const renderIconContainer = (node) => {
    return (
      <CustomListItemIcon>
        {node.sidebarProps?.icon ? (
          <div
            className="d-flex sidebar-icon align-items-center"
            onClick={() => handleIconToggle(node.state)}
          >
            {renderIcon(node.sidebarProps.icon, isDrawerOpen, textColor)}
          </div>
        ) : null}
      </CustomListItemIcon>
    );
  };

  const handleNavigateToPath  = (navigate, menuItem, handleSetModel) => {
    // Set which row to highlight
    setSidebarActiveState(menuItem.state);

    navigateToPath(navigate, menuItem, handleSetModel);
  }

  const handleTextToggle = (nodeId: string, rootState: string) => {
    if (nodeId === rootState) {
      if (expandedNodes[nodeId] === true) {
        setExpandedNodes(() => ({
          [rootState]: false
        }));
      } else {
        setExpandedNodes(() => ({
          [rootState]: true
        }));
      }
    } else {
      setExpandedNodes((prev) => ({
        ...prev,
        [nodeId]: !prev[nodeId],
      }));
    }
  };

  const checkRouteAccess = (route: SidebarType, groups: string[]) => {
    const routeGroups = route.sidebarProps?.adGroups || [];

    // Check if there are no groups required for the route or if user has matching groups
    if (
      routeGroups.length === 0 ||
      routeGroups.some((group) => groups.includes(group))
    ) {
      return true;
    } else {
      return false;
    }
  };

  // Recursive component for rendering nested nodes
  const SideBarItem = ({
    parentMenuItem,
    rootAncestor,
  }: {
    parentMenuItem: SidebarType;
    rootAncestor: string;
  }) => {

    const dispatch = useAppDispatch();

    const handleSetModel = (modelName) => {
      dispatch(setActiveModel(modelName));
    };
    
    return (
      <SidebarItemContainer
        className={
          parentMenuItem.state === activeItem && parentMenuItem.path
            ? "side-item-selected"
            : ""
        }
        ismenuitemactive={
          parentMenuItem.state === activeItem && parentMenuItem.path
            ? "true"
            : "false"
        }
        isDrawerOpen={isDrawerOpen}
      >
        <div
          className={`sidebar-parent-text-container ${ !isDrawerOpen && "px-0" }`}
          onClick={() => {
            setActiveItem(parentMenuItem.state)
            handleTextToggle(parentMenuItem.state, rootAncestor)
            handleNavigateToPath(navigate, parentMenuItem, handleSetModel)
          }}
        >
          {/* Shows the icon if present */}
          <SideBarToolTip
            isIcon={true}
            children={renderIconContainer(parentMenuItem)}
            parent={parentMenuItem}
          />

          <SideBarToolTip
            isIcon={false}
            children={
              <>
                {parentMenuItem.children &&
                parentMenuItem.children.length > 0 &&
                isDrawerOpen ? (
                  <CustomListItemContainer
                    showexpandedmenu={isDrawerOpen}
                    className={`side-item`}
                    // onClick={() => handleNavigateToPath(navigate, parentMenuItem, handleSetModel)}
                    // onClick={() => handleTextToggle(parentMenuItem.state, rootAncestor)}
                  >
                    {/* Shows the name if the menu is expanded */}
                    {isDrawerOpen && (
                      <ListItemText
                        className={"side-item-text"}
                        // onClick={() => handleNavigateToPath(navigate, parentMenuItem, handleSetModel)}
                        primary={
                          <>
                            {parentMenuItem.sidebarProps?.showRawText ? (
                              <Typography>
                                {parentMenuItem.sidebarProps?.displayText}
                              </Typography>
                            ) : (
                              <Typography>
                                {t(
                                  `SIDEBAR.${parentMenuItem.sidebarProps?.displayText}`
                                )}
                              </Typography>
                            )}
                          </>
                          // )
                        }
                      />
                    )}
                  </CustomListItemContainer>
                ) : (
                  <CustomListItemContainer
                    showexpandedmenu={isDrawerOpen}
                    className={`side-item`}
                    // onClick={() => handleNavigateToPath(navigate, parentMenuItem, handleSetModel)}
                  >
                    {/* Shows the name if the menu is expanded */}
                    {isDrawerOpen && (
                      <ListItemText
                        className={"side-item-text"}
                        // onClick={() => handleNavigateToPath(navigate, parentMenuItem, handleSetModel)}
                        primary={
                          <>
                            {parentMenuItem.sidebarProps?.showRawText ? (
                              <Typography>
                                {parentMenuItem.sidebarProps?.displayText}
                              </Typography>
                            ) : (
                              <Typography>
                                {t(
                                  `SIDEBAR.${parentMenuItem.sidebarProps?.displayText}`
                                )}
                              </Typography>
                            )}
                          </>
                          // )
                        }
                      />
                    )}
                  </CustomListItemContainer>
                )}
              </>
            }
            parent={parentMenuItem}
          />
        </div>

        {/* Expandes/Unexpands based on the IcsonButton button above for this level */}
        <Collapse
          in={expandedNodes[parentMenuItem.state]}
          timeout={10000}
          unmountOnExit
        >
          {/* Keep the expansion between nav contractions/expansions */}
          {isDrawerOpen && parentMenuItem.children && (
            <SideBarCollapsedItems>
              {parentMenuItem.children.map((childMenuItem) =>
                checkRouteAccess(childMenuItem, userGroups) === true ? (
                  <SideBarItem
                    key={childMenuItem.state}
                    parentMenuItem={childMenuItem}
                    rootAncestor={rootAncestor}
                  />
                ) : (
                  <div key={childMenuItem.state}></div>
                )
              )}
            </SideBarCollapsedItems>
          )}
        </Collapse>
      </SidebarItemContainer>
    );
  };

  return (
    <SideBarItem
      key={item.state}
      parentMenuItem={item}
      rootAncestor={item.state}
    />
  );
};

export default SidebarItems;
